// Telemetry — lazy-loads the AppSignal SDK for error reports and injects the
// Simple Analytics script for anonymous pageview + event counts.
//
// Privacy posture per docs/strategy/observability.md:
//   - Events are anonymous. No IP, no user id, no email.
//   - Honour Global Privacy Control and DNT as hard opt-outs.
//   - A localStorage flag is the in-app opt-out (synchronous, readable before
//     async IndexedDB has settled at boot).
//   - Breadcrumb URLs are stripped to origin+pathname — query/hash always
//     dropped. Cheaper to whitelist later than to plug leaks reactively.
//   - Empty SDK key = no-op (lets the OSS/empty-prod build skip telemetry).

// Namespace import (not named) so a stale or partial /config.js — older
// deploy still cached by the browser/CDN, partial upload, OSS build with
// fields stripped — leaves missing values as `undefined` instead of raising
// `SyntaxError: Importing binding name '…' is not found` at module
// instantiation. Named imports validate every binding eagerly; that turns a
// missing config field into a whole-bundle outage before the falsy guards
// below ever run. Observability must never be load-bearing for page render.
import * as runtimeConfig from '/config.js';
const {
  appsignalFrontendKey = '',
  simpleAnalyticsHostname = '',
} = runtimeConfig;

const LS_KEY = 'kadnz.telemetry';   // 'off' | unset (= on)

// Loopback or mDNS-local hostname → developer machine; skip telemetry so
// local dev errors don't pollute the staging AppSignal project. The deployed
// staging and prod hosts have public hostnames and aren't affected. Hostname
// is taken as an argument (defaulting to `location.hostname`) so tests can
// exercise this without fighting jsdom's locked-down Location object.
export function isLocalDevHostname(hostname) {
  if (hostname == null) {
    if (typeof location === 'undefined' || !location.hostname) return false;
    hostname = location.hostname;
  }
  return hostname === 'localhost'
      || hostname === '127.0.0.1'
      || hostname === '0.0.0.0'
      || hostname.endsWith('.local');
}

export function telemetryAllowed() {
  if (isLocalDevHostname()) return false;
  if (typeof navigator !== 'undefined') {
    if (navigator.globalPrivacyControl === true) return false;
    if (navigator.doNotTrack === '1') return false;
  }
  try {
    return localStorage.getItem(LS_KEY) !== 'off';
  } catch {
    // localStorage can throw in privacy-restricted contexts. Fail closed.
    return false;
  }
}

export function setTelemetry(on) {
  try {
    if (on) localStorage.removeItem(LS_KEY);
    else localStorage.setItem(LS_KEY, 'off');
  } catch { /* no-op */ }
}

// Reasons the user can't toggle telemetry on from the settings UI. Lets the
// hint text explain why instead of silently disabling the checkbox.
//   'local-dev' — loopback / .local hostname
//   'gpc-dnt'   — browser-level Global Privacy Control or DNT signal
//   null        — toggle is editable
export function telemetryForcedOffReason() {
  if (isLocalDevHostname()) return 'local-dev';
  if (typeof navigator !== 'undefined') {
    if (navigator.globalPrivacyControl === true) return 'gpc-dnt';
    if (navigator.doNotTrack === '1') return 'gpc-dnt';
  }
  return null;
}

export function telemetryForcedOff() {
  return telemetryForcedOffReason() !== null;
}

// Strip a URL down to origin + pathname. Anything in the query string or
// fragment is dropped — that catches OAuth `code`/`state`, custom-URL tokens,
// and any future credential-in-URL pattern without an enumeration.
export function scrubUrl(value) {
  if (typeof value !== 'string' || !value) return value;
  try {
    const url = new URL(value, 'https://placeholder.invalid');
    if (url.hostname === 'placeholder.invalid') return url.pathname; // relative URL
    return `${url.origin}${url.pathname}`;
  } catch {
    return '[unparseable-url]';
  }
}

// Drop request/response bodies and Authorization headers from breadcrumb
// metadata, and scrub any `url`-shaped fields. Mutates in place.
export function scrubBreadcrumb(breadcrumb) {
  if (!breadcrumb || typeof breadcrumb !== 'object') return breadcrumb;
  const meta = breadcrumb.metadata;
  if (!meta || typeof meta !== 'object') return breadcrumb;

  for (const key of Object.keys(meta)) {
    const lower = key.toLowerCase();
    if (lower === 'url' || lower === 'location' || lower === 'href' || lower === 'referrer') {
      meta[key] = scrubUrl(meta[key]);
    } else if (lower === 'body' || lower === 'requestbody' || lower === 'responsebody' || lower === 'data') {
      delete meta[key];
    } else if (lower === 'authorization' || lower === 'auth') {
      delete meta[key];
    }
  }
  return breadcrumb;
}

// Decorator runs on every span before send. Drop the span entirely if the
// user toggled off mid-session; otherwise scrub each breadcrumb.
function scrubbingDecorator(span) {
  if (!telemetryAllowed()) return span.setBreadcrumbs([]);
  const breadcrumbs = span.getBreadcrumbs() || [];
  span.setBreadcrumbs(breadcrumbs.map(scrubBreadcrumb));
  return span;
}

let _client = null;

// Lazily import the SDK and install global error handlers. No-op if telemetry
// is disabled, the SDK key is empty, or the client is already initialised.
// `revision` is the build SHA so AppSignal groups issues across deploys.
export async function initTelemetry({ revision } = {}) {
  if (_client) return _client;
  if (!telemetryAllowed()) return null;
  if (!appsignalFrontendKey) return null;

  const [{ default: Appsignal }, { plugin: windowEventsPlugin }] = await Promise.all([
    import('@appsignal/javascript'),
    import('@appsignal/plugin-window-events'),
  ]);

  _client = new Appsignal({
    key: appsignalFrontendKey,
    revision: revision || '',
  });
  _client.addDecorator(scrubbingDecorator);
  _client.use(windowEventsPlugin());
  return _client;
}

// Report an error that the calling code already caught. The window-events
// plugin only fires for *uncaught* errors, so anything in a try/catch never
// reaches AppSignal automatically — call this from the catch block.
//
// Gated through `telemetryAllowed()` and the SDK presence so opt-out, GPC,
// local-dev, and "SDK never initialised" all short-circuit transparently.
// Tag values are stringified to satisfy the SDK's HashMap<string> contract.
export function reportError(errorOrMessage, tags = {}) {
  if (!telemetryAllowed() || !_client) return;
  const error = errorOrMessage instanceof Error
    ? errorOrMessage
    : new Error(String(errorOrMessage));
  try {
    const stringTags = Object.fromEntries(
      Object.entries(tags).map(([k, v]) => [k, String(v)]),
    );
    const span = _client.createSpan(s => {
      s.setError(error);
      if (Object.keys(stringTags).length) s.setTags(stringTags);
    });
    _client.send(span);
  } catch { /* swallow — telemetry must never break the calling code */ }
}

// ── Simple Analytics ─────────────────────────────────────────────────────────
// Anonymous visit + event counts. Same opt-out gate as the AppSignal track —
// telemetryAllowed() is the single switch. Loaded as a CDN script tag (not an
// npm package) because that's the SA-documented integration path and lets the
// script tag carry the data-collect-dnt attribute the vendor reads directly.

const SA_SRC = 'https://scripts.simpleanalyticscdn.com/latest.js';

let _saScriptInjected = false;

// Inject the SA loader script. No-op when telemetry is disallowed, the
// hostname isn't configured (OSS/empty-prod build), or the script has already
// been injected this session.
export function initAnalytics() {
  if (_saScriptInjected) return;
  if (!telemetryAllowed()) return;
  if (!simpleAnalyticsHostname) return;
  if (typeof document === 'undefined') return;

  const script = document.createElement('script');
  script.async = true;
  script.defer = true;
  script.src = SA_SRC;
  // SA respects DNT independently of our own gate when this attr is set;
  // belt-and-braces alongside telemetryAllowed().
  script.setAttribute('data-collect-dnt', 'true');
  script.setAttribute('data-hostname', simpleAnalyticsHostname);
  document.head.appendChild(script);
  _saScriptInjected = true;
}

// Fire a named SA custom event. No-op when telemetry is disallowed (catches a
// mid-session opt-out even though the script may still be loaded) or when SA
// hasn't installed its global yet. Swallows its own throws — telemetry must
// never break the calling code (same contract as reportError).
export function trackEvent(name, metadata = {}) {
  if (!telemetryAllowed()) return;
  if (typeof window === 'undefined') return;
  const sa = window.sa_event;
  if (typeof sa !== 'function') return;
  try {
    if (metadata && Object.keys(metadata).length) sa(name, metadata);
    else sa(name);
  } catch { /* swallow */ }
}

// Test-only escape hatch — lets unit tests reset module state between cases.
export function _resetForTests() {
  _client = null;
  _saScriptInjected = false;
}

// Test-only escape hatch — lets unit tests inject a fake client without
// loading the real SDK over the network.
export function _setClientForTests(client) {
  _client = client;
}
